import {
  Box,
  Card,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import UploadDocumentButton from '@/karehero-app/shell/UploadDocumentButton';
import { type ActionData } from '@/karehero-app/actions/ActionDetail';

const ASSIGNEES = ['You', 'Abbie', 'Yasmin'];
const UNITS = ['Days', 'Weeks', 'Months'];

// Editing is deliberately split (per the Figma board's note): editing a
// single action (a one-off, or one occurrence of a series) edits its date;
// editing the series edits frequency and the date the change takes effect
// from. Neither offers a repeat on/off switch: turning a repeat on or off is
// not an edit, which avoids the edge cases around editing-and-unrepeating.
// Only the creator ever sees Edit at all.
const EditAction = ({
  action,
  series = false,
  onClose,
}: {
  action: ActionData;
  /** Editing the whole repeating series rather than this occurrence. */
  series?: boolean;
  onClose: () => void;
}) => {
  const recurring = series;

  const footer = (
    <Button
      variant='primary'
      size='lg'
      isFullWidth
      ariaLabel='Save changes'
      onPress={onClose}
    >
      Save changes
    </Button>
  );

  return (
    <BottomSheet
      title='Edit action'
      titleAlign='center'
      onClose={onClose}
      footer={footer}
    >
      <Stack sx={{ pt: 0.5, pb: 2 }} spacing={2}>
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Title'
          defaultValue={action.title}
          fullWidth
        />

        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          select
          fullWidth
          label='Assignee'
          defaultValue='You'
        >
          {ASSIGNEES.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>

        {recurring ? (
          <>
            {/* the series, grouped in a card like the design: how often,
                and the date the edit takes effect from */}
            <Card sx={{ p: 2 }}>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ display: 'block', mb: 1 }}
              >
                Every
              </Typography>
              <Stack direction='row' spacing={1.5}>
                <TextField
                  slotProps={{ inputLabel: { shrink: true } }}
                  defaultValue='2'
                  sx={{ width: 88 }}
                />
                <TextField select fullWidth defaultValue='Weeks'>
                  {UNITS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Card>
            <TextField
              slotProps={{ inputLabel: { shrink: true } }}
              label='Effective from'
              defaultValue={action.due}
              fullWidth
            />
          </>
        ) : (
          <TextField
            slotProps={{ inputLabel: { shrink: true } }}
            label='Date'
            defaultValue={action.due}
            fullWidth
          />
        )}

        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Time'
          fullWidth
        />

        {/* uploads */}
        <Box>
          <Typography
            variant='overline'
            color='text.secondary'
            sx={{ display: 'block', mb: 1 }}
          >
            UPLOADS
          </Typography>
          <Stack spacing={1.5}>
            <UploadDocumentButton />
            {action.files.map((f, i) => (
              <Card key={i} sx={{ p: 2 }}>
                <Stack
                  sx={{ alignItems: 'center' }}
                  direction='row'
                  spacing={1.5}
                >
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>
                    <Icon icon='file-lines' fontSize={20} />
                  </Box>
                  <Typography
                    sx={{ flex: 1, minWidth: 0 }}
                    variant='body1'
                    noWrap
                  >
                    {f}
                  </Typography>
                  <Box sx={{ color: 'custom.neutral.500', display: 'flex' }}>
                    <Icon icon={'ellipsis-vertical' as never} fontSize={18} />
                  </Box>
                </Stack>
              </Card>
            ))}
          </Stack>
        </Box>
      </Stack>
    </BottomSheet>
  );
};

export default EditAction;
